import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  Chip,
  IconButton,
} from "@mui/material";
import { KeyboardArrowDown, Add } from "@mui/icons-material";

export default function AccessoriesCard({
  open,
  onClose,
  selectedAccessories,
  onToggleAccessory,
  accessoriesData = [],
}) {
  const [activeTab, setActiveTab] = useState(1);
  const [selectedFilters, setSelectedFilters] = useState(["All"]);

  const tabs = ["All", "Exterior", "Lifestyle", "Interior"];
  const filters = [
    "All",
    "Mouldings",
    "Body Graphics",
    "Door Visor Set",
    "Spoilers",
    "Mud Flaps",
  ];

  const filteredAccessories = useMemo(() => {
    let filtered = accessoriesData;

    // Filter by active tab (based on accessory_type)
    if (tabs[activeTab] !== "All") {
      filtered = filtered.filter(
        (acc) =>
          acc.accessory_type?.toLowerCase() === tabs[activeTab].toLowerCase()
      );
    }

    // Filter by selected tags
    if (!selectedFilters.includes("All")) {
      filtered = filtered.filter((acc) =>
        acc.accessory_tags?.some((tag) => selectedFilters.includes(tag))
      );
    }

    return filtered;
  }, [accessoriesData, activeTab, selectedFilters]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleFilterClick = (filter) => {
    if (filter === "All") {
      setSelectedFilters(["All"]);
    } else {
      const newFilters = selectedFilters.includes(filter)
        ? selectedFilters.filter((f) => f !== filter)
        : [...selectedFilters.filter((f) => f !== "All"), filter];

      setSelectedFilters(newFilters.length === 0 ? ["All"] : newFilters);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxWidth: 500,
          maxHeight: "80vh",
          m: 0,
          position: "fixed",
          bottom: 120,
          right: 20,
          top: "auto",
          left: "auto",
          transform: "none",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "transparent",
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          alignItems: "flex-end",
          justifyContent: "flex-end",
        },
      }}
    >
      <DialogContent sx={{ p: 0, height: "100%" }}>
        {/* Header */}
        <Box sx={{ p: 3, pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", fontSize: "1.5rem" }}
            >
              Accessories
            </Typography>
            <KeyboardArrowDown sx={{ color: "text.secondary" }} />
          </Box>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontSize: "0.875rem" }}
          >
            Choose from our exclusive selection of accessories for Celerio.
          </Typography>
        </Box>

        {/* Tabs */}
        <Box sx={{ px: 3, mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": {
                minWidth: "auto",
                px: 2,
                fontSize: "0.875rem",
                textTransform: "none",
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab key={index} label={tab} />
            ))}
          </Tabs>
        </Box>

        {/* Filter Chips */}
        <Box sx={{ px: 3, mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
            }}
          >
            {filters.map((filter) => (
              <Chip
                key={filter}
                label={filter}
                variant={
                  selectedFilters.includes(filter) ? "filled" : "outlined"
                }
                size="small"
                onClick={() => handleFilterClick(filter)}
                sx={{
                  fontSize: "0.75rem",
                  height: 28,
                  bgcolor: selectedFilters.includes(filter)
                    ? "primary.main"
                    : "transparent",
                  color: selectedFilters.includes(filter)
                    ? "white"
                    : "text.primary",
                  "&:hover": {
                    bgcolor: selectedFilters.includes(filter)
                      ? "primary.dark"
                      : "action.hover",
                  },
                }}
              />
            ))}
            <IconButton
              size="small"
              sx={{ width: 28, height: 28, border: "1px dashed #ccc" }}
            >
              <Add sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Accessories List */}
        <Box sx={{ px: 3, pb: 3, maxHeight: 400, overflowY: "auto" }}>
          {filteredAccessories.map((accessory) => (
            <Box
              key={accessory.accessory_id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                py: 2,
                borderBottom: "1px solid #f0f0f0",
                "&:last-child": {
                  borderBottom: "none",
                },
              }}
            >
              <Box
                component="img"
                src={
                  accessory.accessory_icons.find((i) => i.file_type === "S")
                    ?.path || "/placeholder.svg"
                }
                alt={accessory.display_name}
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 1,
                  objectFit: "cover",
                  bgcolor: "#f5f5f5",
                }}
              />

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "500",
                    fontSize: "0.875rem",
                    lineHeight: 1.3,
                    mb: 1,
                  }}
                >
                  {accessory.display_name}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1rem",
                    color: "text.primary",
                  }}
                >
                  ₹{accessory.price}
                </Typography>
              </Box>

              <Button
                variant={
                  selectedAccessories.some(
                    (a) => a.accessory_id === accessory.accessory_id
                  )
                    ? "contained"
                    : "outlined"
                }
                color="primary"
                size="small"
                startIcon={<Add />}
                onClick={() => onToggleAccessory(accessory)}
                sx={{
                  minWidth: 60,
                  fontSize: "0.75rem",
                  textTransform: "none",
                  borderRadius: 20,
                  px: 2,
                }}
              >
                {selectedAccessories.some(
                  (a) => a.accessory_id === accessory.accessory_id
                )
                  ? "Added"
                  : "Add"}
              </Button>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
